/**
 * @file src/components/atoms/Tray/TrayBody.tsx
 * @description This file defines the TrayBody component, which serves as the content area within a Tray component.
 * The TrayBody component can display a headline, a caption, and additional children elements. It provides a flexible layout
 * that adapts to various content types and ensures a consistent style across the application.
 * @module TrayBody
 */

import React, { FC } from 'react';
import { TrayBodyProps } from './interfaces/Tray';
import { Text } from '@/components/atoms';
import APP_CONFIG from '@/config/app';

const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';

const {
  MAIN_GAP,
  WHOLESTEP_DECREMENT,
  TEXT: { SMALL_HEADING, SMALL_HEADING_COLOR, BODY, BODY_COLOR },
} = APP_CONFIG.STYLES;

const spacingConfig = {
  padding: 'p-0',
  gap: `gap${MAIN_GAP}`,
  bodyPadding: 'p-0',
  bodyGap: `gap${MAIN_GAP}`,
  bodyHeadlinePadding: 'p-0',
  bodyHeadlineGap: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
};

const textConfig = {
  headlineTitleSize: SMALL_HEADING,
  headlineTitleColor: SMALL_HEADING_COLOR,
  headlineCaptionSize: BODY,
  headlineCaptionColor: BODY_COLOR,
};

const sizingConfig = {
  width: 'w-full',
  height: 'h-full',
  bodyWidth: 'w-full',
  bodyHeight: 'h-full',
  bodyHeadlineWidth: 'w-full',
};

const TrayBody: FC<TrayBodyProps> = ({
  children,
  headlineTitle,
  headlineCaption,
}: TrayBodyProps): JSX.Element => {
  return (
    <div
      className={`flex flex-col flex-1 overflow-hidden 
        ${sizingConfig.width} 
        ${sizingConfig.height} 
        ${spacingConfig.padding} 
        ${spacingConfig.gap} 
        ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {(headlineTitle || headlineCaption) && (
        <div
          className={`flex flex-col items-center 
            ${spacingConfig.bodyHeadlinePadding} 
            ${spacingConfig.bodyHeadlineGap} 
            ${sizingConfig.bodyHeadlineWidth} 
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {headlineTitle && typeof headlineTitle === 'string' ? (
            <Text
              value={headlineTitle}
              size={textConfig.headlineTitleSize}
              color={textConfig.headlineTitleColor}
            />
          ) : (
            headlineTitle
          )}
          {headlineCaption && typeof headlineCaption === 'string' ? (
            <Text
              value={headlineCaption}
              size={textConfig.headlineCaptionSize}
              color={textConfig.headlineCaptionColor}
            />
          ) : (
            headlineCaption
          )}
        </div>
      )}
      <div
        className={`flex-1 overflow-y-auto scrollbar-hide 
          ${spacingConfig.bodyGap} 
          ${spacingConfig.bodyPadding} 
          ${sizingConfig.bodyWidth} 
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {children}
      </div>
    </div>
  );
};

export default TrayBody;
